import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Inbox, RefreshCw, Lock, Search, ShieldCheck, XCircle, Undo2, FlaskConical } from "lucide-react";
import { COUNTRIES as PILOT_COUNTRY_LIST, countryLabel } from "@/constants/countries";

/**
 * A20-P10 — Admin Marketplace Review.
 *
 * Path: /admin/marketplace-requests
 * Roles: admin, staff only (also enforced by backend + AdminLayout sidebar).
 *
 * The page is a private review surface — nothing is ever published. The
 * `approved_internal` terminal state is an internal validation marker
 * only; vendors / sponsors / offers / payments stay completely out of
 * the picture.
 */

const STATUS_OPTIONS = [
  "draft", "ready_for_review", "internal_review",
  "approved_internal", "offer_selected", "rejected", "cancelled", "archived",
];
const PILOT_COUNTRIES = PILOT_COUNTRY_LIST.map((c) => c.code);
const CATEGORIES = [
  "equipment", "travel", "hotel", "transport", "food", "event_access",
  "training", "coaching", "medical", "physiotherapy", "technology",
  "marketing", "staff", "facilities", "insurance", "documentation",
  "gifts", "contingency", "other",
];
const OWNER_PROFILES = [
  "club_director", "coach", "head_coach", "player",
  "parent_guardian", "federation_manager", "fitness_coach",
  "sports_doctor", "physiotherapist", "sport_psychologist",
];

const STATUS_PILL = {
  draft:             "bg-slate-100 text-slate-800",
  ready_for_review:  "bg-amber-100 text-amber-900",
  internal_review:   "bg-sky-100 text-sky-900",
  approved_internal: "bg-emerald-100 text-emerald-900",
  offer_selected:    "bg-emerald-200 text-emerald-900 ring-1 ring-emerald-400",
  rejected:          "bg-rose-100 text-rose-900",
  cancelled:         "bg-rose-100 text-rose-900",
  archived:          "bg-slate-200 text-slate-600",
};

const fmtEur = (n) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: "EUR", maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `€${Math.round(Number(n) || 0)}`; }
};

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toISOString().slice(0, 10); }
  catch { return String(s).slice(0, 10); }
};

// A20-P13 — admin drilldown summary for the selected offer. Lazy-fetches
// the full admin offer document (which includes vendor_id + staff_notes
// — both admin-only) and renders a tight info block. Self-contained so
// AdminMarketplaceRequests doesn't need extra state.
const SelectedOfferSummary = ({ offerId }) => {
  const { t } = useLang();
  const [offer, setOffer] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    api.get(`/admin/offers/${offerId}`)
      .then((r) => { if (!cancelled) setOffer(r.data?.offer || r.data); })
      .catch((e) => { if (!cancelled) setErr(extractErrorMessage(e, "")); });
    return () => { cancelled = true; };
  }, [offerId]);
  if (err) return <div className="text-xs text-rose-700">{err}</div>;
  if (!offer) return <div className="text-xs text-slate-500">…</div>;
  return (
    <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs"
        data-testid="admin-mkt-selected-offer-detail">
      <dt className="text-emerald-900/70 font-medium">{t("adminMarketplace.drawer.selectedOffer.vendor")}</dt>
      <dd className="text-emerald-900 font-semibold">{offer.vendor_name_snapshot || "—"}</dd>
      <dt className="text-emerald-900/70 font-medium">{t("adminMarketplace.drawer.selectedOffer.price")}</dt>
      <dd className="text-emerald-900 font-semibold tabular-nums">{fmtEur(offer.offered_price)}</dd>
      <dt className="text-emerald-900/70 font-medium">{t("adminMarketplace.drawer.selectedOffer.expires")}</dt>
      <dd className="text-emerald-900 font-mono">{offer.expiration_date || "—"}</dd>
      <dt className="text-emerald-900/70 font-medium">{t("adminMarketplace.drawer.selectedOffer.selectedAt")}</dt>
      <dd className="text-emerald-900 font-mono">{fmtDate(offer.selected_at)}</dd>
      <dt className="text-emerald-900/70 font-medium">{t("adminMarketplace.drawer.selectedOffer.offerId")}</dt>
      <dd className="text-emerald-900/70 font-mono text-[10px]">{offer.id}</dd>
    </dl>
  );
};

const AdminMarketplaceRequests = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  // Filters
  const [filterStatus, setFilterStatus] = useState("ready_for_review,internal_review");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterOwnerProfile, setFilterOwnerProfile] = useState("");
  const [searchQ, setSearchQ] = useState("");

  // Drawer
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [reasonInput, setReasonInput] = useState("");
  const [acting, setActing] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set("status", filterStatus);
      if (filterCountry) params.set("country", filterCountry);
      if (filterCategory) params.set("category", filterCategory);
      if (filterOwnerProfile) params.set("owner_profile", filterOwnerProfile);
      if (searchQ.trim()) params.set("q", searchQ.trim());
      params.set("limit", String(limit));
      params.set("offset", String(offset));
      const r = await api.get(`/admin/marketplace-requests?${params.toString()}`);
      setRows(r.data?.requests || []);
      setTotal(Number(r.data?.total || 0));
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminMarketplace.toasts.failed")));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCountry, filterCategory, filterOwnerProfile, searchQ, offset, t]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openDrawer = async (req) => {
    setOpenId(req.id);
    setDetail(null);
    setAuditEntries([]);
    setReasonInput("");
    try {
      const [d, a] = await Promise.all([
        api.get(`/admin/marketplace-requests/${req.id}`),
        api.get(`/admin/marketplace-requests/${req.id}/audit-log`),
      ]);
      setDetail(d.data);
      setAuditEntries(a.data?.entries || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminMarketplace.toasts.failed")));
    }
  };

  const closeDrawer = () => {
    setOpenId(null);
    setDetail(null);
    setAuditEntries([]);
    setReasonInput("");
  };

  const runAction = async (action) => {
    if (!openId) return;
    setActing(true);
    try {
      let resp;
      if (action === "start_review") {
        resp = await api.post(`/admin/marketplace-requests/${openId}/start-review`, {});
      } else {
        const payload = { action, review_reason: reasonInput.trim() || null };
        resp = await api.post(`/admin/marketplace-requests/${openId}/review`, payload);
      }
      const updated = resp.data?.request;
      // Update the table row in place
      if (updated) {
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setDetail((prev) => prev ? { ...prev, request: updated } : prev);
      }
      // Refresh audit log
      const a = await api.get(`/admin/marketplace-requests/${openId}/audit-log`);
      setAuditEntries(a.data?.entries || []);
      const toastMap = {
        start_review: "startReviewed",
        approve_internal: "approved",
        reject: "rejected",
        return_to_draft: "returned",
      };
      toast.success(t(`adminMarketplace.toasts.${toastMap[action]}`));
      setReasonInput("");
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminMarketplace.toasts.failed")));
    } finally {
      setActing(false);
    }
  };

  const currentStatus = detail?.request?.status;
  const canStartReview = currentStatus === "ready_for_review";
  const canApprove = ["ready_for_review", "internal_review"].includes(currentStatus);
  const canReject = ["ready_for_review", "internal_review", "approved_internal"].includes(currentStatus);
  const canReturn = ["ready_for_review", "internal_review", "approved_internal", "rejected"].includes(currentStatus);
  const reasonRequiredForCurrentMode = false; // each button controls its own
  const reasonRequired = reasonInput.trim().length === 0;

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total]);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-6" data-testid="admin-mkt-page">
      <header className="space-y-2">
        <h1 className="text-2xl font-display font-bold inline-flex items-center gap-2 text-slate-900">
          <Inbox className="w-6 h-6 text-fuchsia-700" />
          {t("adminMarketplace.pageTitle")}
        </h1>
        <p className="text-sm text-slate-600">{t("adminMarketplace.pageSub")}</p>
        <p className="text-[11px] text-slate-400 inline-flex items-center gap-1">
          <Lock className="w-3 h-3" />
          {t("adminMarketplace.privacy")}
        </p>
      </header>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <label className="block text-xs">
            <span className="block text-slate-600 mb-1">{t("adminMarketplace.filters.status")}</span>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setOffset(0); }}
              data-testid="admin-mkt-filter-status"
            >
              <option value="ready_for_review,internal_review">{`${t("adminMarketplace.filters.all")} (review)`}</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{t(`marketplace.status.${s}`)}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            <span className="block text-slate-600 mb-1">{t("adminMarketplace.filters.country")}</span>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={filterCountry}
              onChange={(e) => { setFilterCountry(e.target.value); setOffset(0); }}
              data-testid="admin-mkt-filter-country"
            >
              <option value="">{t("adminMarketplace.filters.all")}</option>
              {PILOT_COUNTRIES.map((c) => (
                <option key={c} value={c}>{countryLabel(c, t)} ({c})</option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            <span className="block text-slate-600 mb-1">{t("adminMarketplace.filters.category")}</span>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setOffset(0); }}
              data-testid="admin-mkt-filter-category"
            >
              <option value="">{t("adminMarketplace.filters.all")}</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block text-xs">
            <span className="block text-slate-600 mb-1">{t("adminMarketplace.filters.owner_profile")}</span>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={filterOwnerProfile}
              onChange={(e) => { setFilterOwnerProfile(e.target.value); setOffset(0); }}
              data-testid="admin-mkt-filter-owner-profile"
            >
              <option value="">{t("adminMarketplace.filters.all")}</option>
              {OWNER_PROFILES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="block text-xs">
            <span className="block text-slate-600 mb-1">{t("adminMarketplace.filters.search")}</span>
            <div className="relative">
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setOffset(0); fetchList(); } }}
                className="ps-7 text-sm"
                data-testid="admin-mkt-search-input"
              />
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-2.5" />
            </div>
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="admin-mkt-empty">
              {t("adminMarketplace.empty")}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-mkt-table">
              <thead className="bg-slate-50 text-start text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t("adminMarketplace.table.title")}</th>
                  <th className="px-3 py-2">{t("adminMarketplace.table.owner")}</th>
                  <th className="px-3 py-2">{t("adminMarketplace.table.country")}</th>
                  <th className="px-3 py-2">{t("adminMarketplace.table.category")}</th>
                  <th className="px-3 py-2 text-end">{t("adminMarketplace.table.budget")}</th>
                  <th className="px-3 py-2">{t("adminMarketplace.table.requiredBy")}</th>
                  <th className="px-3 py-2">{t("adminMarketplace.table.status")}</th>
                  <th className="px-3 py-2">{t("adminMarketplace.table.updated")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((req) => (
                  <tr
                    key={req.id}
                    className="border-t border-slate-100 hover:bg-fuchsia-50/50 cursor-pointer transition"
                    onClick={() => openDrawer(req)}
                    data-testid={`admin-mkt-row-${req.id}`}
                  >
                    <td className="px-3 py-2 max-w-xs truncate font-medium text-slate-800">{req.title}</td>
                    <td className="px-3 py-2 text-slate-600">{req.owner_profile || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{req.country || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{req.category || "—"}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{fmtEur(req.max_budget)}</td>
                    <td className="px-3 py-2 text-slate-600 tabular-nums">{fmtDate(req.required_by_date)}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${STATUS_PILL[req.status] || "bg-slate-100"} border-0 text-[10px] uppercase`}>
                        {t(`marketplace.status.${req.status}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs tabular-nums">{fmtDate(req.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination + refresh */}
      <div className="flex items-center justify-between gap-2 text-sm">
        <Button variant="outline" size="sm" onClick={fetchList} disabled={loading}
          data-testid="admin-mkt-refresh">
          <RefreshCw className="w-3.5 h-3.5 me-1" /> {t("adminMarketplace.actions.refresh")}
        </Button>
        <div className="inline-flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0 || loading}
            data-testid="admin-mkt-pagination-prev"
          >
            {t("adminMarketplace.pagination.prev")}
          </Button>
          <span className="text-slate-500 tabular-nums">
            {currentPage} {t("adminMarketplace.pagination.of")} {totalPages}
          </span>
          <Button
            variant="outline" size="sm"
            onClick={() => setOffset(offset + limit)}
            disabled={(offset + limit) >= total || loading}
            data-testid="admin-mkt-pagination-next"
          >
            {t("adminMarketplace.pagination.next")}
          </Button>
        </div>
      </div>

      {/* Drawer */}
      <Sheet open={!!openId} onOpenChange={(o) => { if (!o) closeDrawer(); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid="admin-mkt-drawer">
          <SheetHeader>
            <SheetTitle className="inline-flex items-center gap-2">
              {detail?.request?.title || "—"}
              {currentStatus && (
                <Badge className={`${STATUS_PILL[currentStatus] || "bg-slate-100"} border-0 text-[10px] uppercase`}>
                  {t(`marketplace.status.${currentStatus}`)}
                </Badge>
              )}
            </SheetTitle>
          </SheetHeader>
          {!detail ? (
            <p className="text-sm text-slate-500 mt-4">Loading…</p>
          ) : (
            <div className="space-y-5 mt-4 text-sm">
              <Badge className="bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-200 text-[10px]">
                <Lock className="w-3 h-3 me-1" /> {t("marketplace.privateDraftBadge")} · {t("marketplace.notPublished")}
              </Badge>

              {/* Source block */}
              <section data-testid="admin-mkt-source-block">
                <h3 className="font-semibold text-slate-800 mb-2 text-sm">
                  {t("adminMarketplace.drawer.source.heading")}
                </h3>
                {detail.source?.wishlist ? (
                  <div className="border rounded-lg p-3 space-y-1 text-xs">
                    <div><span className="text-slate-500">{t("adminMarketplace.drawer.source.wishlist")}:</span> <strong>{detail.source.wishlist.title}</strong></div>
                    {detail.source.item && (
                      <>
                        <div><span className="text-slate-500">{t("adminMarketplace.drawer.source.item")}:</span> <strong>{detail.source.item.title}</strong></div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="bg-slate-100 px-2 py-0.5 rounded">{t("adminMarketplace.drawer.source.itemCategory")}: {detail.source.item.category}</span>
                          <span className="bg-slate-100 px-2 py-0.5 rounded">{t("adminMarketplace.drawer.source.itemQuantity")}: {detail.source.item.quantity}</span>
                          <span className="bg-slate-100 px-2 py-0.5 rounded">{t("adminMarketplace.drawer.source.itemMaxBudget")}: {fmtEur(detail.source.item.max_budget)}</span>
                          {detail.source.item.required_by_date && (
                            <span className="bg-slate-100 px-2 py-0.5 rounded">{t("adminMarketplace.drawer.source.itemRequiredBy")}: {fmtDate(detail.source.item.required_by_date)}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">{t("adminMarketplace.drawer.source.missing")}</p>
                )}
              </section>

              {/* Owner block */}
              <section data-testid="admin-mkt-owner-block">
                <h3 className="font-semibold text-slate-800 mb-2 text-sm inline-flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
                  {t("adminMarketplace.drawer.owner.heading")}
                </h3>
                <div className="border rounded-lg p-3 space-y-1 text-xs">
                  <div><span className="text-slate-500">{t("adminMarketplace.drawer.owner.email")}:</span> {detail.owner?.email || "—"}</div>
                  <div><span className="text-slate-500">{t("adminMarketplace.drawer.owner.profile")}:</span> {detail.owner?.profile_key || "—"}</div>
                  <div><span className="text-slate-500">{t("adminMarketplace.drawer.owner.country")}:</span> {detail.owner?.country || "—"}</div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">{t("adminMarketplace.drawer.owner.noPii")}</p>
                {detail.owner?.profile_key === "player" && detail.owner?.is_minor && (
                  <Badge className="bg-amber-100 text-amber-900 mt-2"
                    data-testid="admin-mkt-minor-warning-chip">
                    <FlaskConical className="w-3 h-3 me-1" />
                    {t("adminMarketplace.drawer.minorWarning")}
                  </Badge>
                )}
              </section>

              {/* Review reason */}
              <section>
                <label className="block text-sm">
                  <span className="block text-slate-700 font-medium mb-1">
                    {t("adminMarketplace.drawer.reviewReason.label")}
                  </span>
                  <Textarea
                    rows={3}
                    maxLength={2000}
                    value={reasonInput}
                    onChange={(e) => setReasonInput(e.target.value)}
                    placeholder={t("adminMarketplace.drawer.reviewReason.placeholderRequired")}
                    data-testid="admin-mkt-reason-input"
                  />
                </label>
                <p className="text-[11px] text-slate-400 mt-1">
                  {t("adminMarketplace.drawer.reviewReason.placeholderOptional")}
                </p>
              </section>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {canStartReview && (
                  <Button
                    onClick={() => runAction("start_review")}
                    disabled={acting}
                    variant="outline"
                    data-testid="admin-mkt-action-start-review"
                  >
                    <RefreshCw className="w-3.5 h-3.5 me-1" />
                    {t("adminMarketplace.actions.startReview")}
                  </Button>
                )}
                {canApprove && (
                  <Button
                    onClick={() => runAction("approve_internal")}
                    disabled={acting}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="admin-mkt-action-approve"
                  >
                    <ShieldCheck className="w-3.5 h-3.5 me-1" />
                    {t("adminMarketplace.actions.approveInternal")}
                  </Button>
                )}
                {canReject && (
                  <Button
                    onClick={() => runAction("reject")}
                    disabled={acting || reasonRequired}
                    variant="destructive"
                    data-testid="admin-mkt-action-reject"
                  >
                    <XCircle className="w-3.5 h-3.5 me-1" />
                    {t("adminMarketplace.actions.reject")}
                  </Button>
                )}
                {canReturn && (
                  <Button
                    onClick={() => runAction("return_to_draft")}
                    disabled={acting || reasonRequired}
                    variant="outline"
                    className="border-amber-300 text-amber-900 hover:bg-amber-50"
                    data-testid="admin-mkt-action-return"
                  >
                    <Undo2 className="w-3.5 h-3.5 me-1" />
                    {t("adminMarketplace.actions.returnToDraft")}
                  </Button>
                )}
                {!canStartReview && !canApprove && !canReject && !canReturn && reasonRequiredForCurrentMode && null}
                <div className="flex-1" />
                <Button variant="ghost" onClick={closeDrawer} data-testid="admin-mkt-action-close">
                  {t("adminMarketplace.actions.close")}
                </Button>
              </div>

              {/* A20-P13 — Selected offer summary. Admin needs to see at a
                  glance which vendor the owner chose, so the follow-up is
                  one click away. No PII, no payment, no contact CTA. */}
              {detail.request.selected_offer_id && (
                <section
                  data-testid="admin-mkt-selected-offer"
                  className="rounded-md border border-emerald-300 bg-emerald-50 p-3"
                >
                  <h3 className="font-semibold text-emerald-900 text-sm mb-1 inline-flex items-center gap-1">
                    {t("adminMarketplace.drawer.selectedOffer.heading")}
                  </h3>
                  <p className="text-xs text-emerald-900/80 mb-2">
                    {t("adminMarketplace.drawer.selectedOffer.subtitle")}
                  </p>
                  <SelectedOfferSummary offerId={detail.request.selected_offer_id} />
                </section>
              )}

              {/* Audit log */}
              <section data-testid="admin-mkt-audit-log">
                <h3 className="font-semibold text-slate-800 mb-2 text-sm">
                  {t("adminMarketplace.drawer.auditLog.heading")}
                </h3>
                {auditEntries.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">{t("adminMarketplace.drawer.auditLog.empty")}</p>
                ) : (
                  <ul className="space-y-2 text-xs">
                    {auditEntries.map((e) => (
                      <li
                        key={e.id}
                        className="border-l-2 border-fuchsia-200 ps-3 py-1"
                        data-testid={`admin-mkt-audit-row-${e.id}`}
                      >
                        <div className="font-medium text-slate-800">
                          {t(`adminMarketplace.drawer.auditLog.events.${e.event}`) || e.event}
                        </div>
                        <div className="text-slate-500">
                          {(e.actor_email || "—")} · {fmtDate(e.at)}
                        </div>
                        {(e.from_status || e.to_status) && (
                          <div className="text-slate-400 tabular-nums">
                            {e.from_status || "—"} → {e.to_status || "—"}
                          </div>
                        )}
                        {e.reason && (
                          <div className="text-slate-600 mt-1 bg-slate-50 px-2 py-1 rounded italic">
                            "{e.reason}"
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminMarketplaceRequests;
